import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { TextField, Button, CircularProgress, Grid } from "@mui/material";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { toast } from "react-toastify";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import axios from "utils/api";

const formatDateTimeLocalValue = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  // `datetime-local` expects local time without a timezone suffix.
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const MeetingDialog = ({ open, onClose, tutorId, existingMeeting, onSuccess }) => {
  const [formData, setFormData] = useState({
    meetingLink: "",
    duration: 30,
    scheduledDate: "",
    meetingNotes: "",
  });
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (existingMeeting) {
      setIsEdit(true);
      setFormData({
        meetingLink: existingMeeting.meetingLink || "",
        duration: existingMeeting.duration || 30,
        scheduledDate: formatDateTimeLocalValue(existingMeeting.scheduledDate),
        meetingNotes: existingMeeting.meetingNotes || "",
      });
    } else {
      setIsEdit(false);
      setFormData({
        meetingLink: "",
        duration: 30,
        scheduledDate: "",
        meetingNotes: "",
      });
    }
  }, [existingMeeting, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.meetingLink || !formData.scheduledDate) {
      toast.error("Meeting link and scheduled date are required!");
      return;
    }

    try {
      setLoading(true);

      // Get current logged-in admin ID
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        toast.error("User not found. Please login again.");
        setLoading(false);
        return;
      }
      const user = JSON.parse(userStr);
      const adminId = user._id || user.id;

      let response;
      if (isEdit && existingMeeting?._id) {
        // Update existing meeting - don't send tutorId
        const updatePayload = {
          adminId,
          meetingLink: formData.meetingLink,
          duration: parseInt(formData.duration) || 30,
          scheduledDate: new Date(formData.scheduledDate).toISOString(),
          meetingNotes: formData.meetingNotes,
          meetingType: "VIRTUAL_INTERVIEW",
        };
        response = await axios.put(`/meetings/${existingMeeting._id}`, updatePayload);
      } else {
        // Create new meeting - include tutorId
        const createPayload = {
          tutorId,
          adminId,
          meetingLink: formData.meetingLink,
          duration: parseInt(formData.duration) || 30,
          scheduledDate: new Date(formData.scheduledDate).toISOString(),
          meetingNotes: formData.meetingNotes,
          meetingType: "VIRTUAL_INTERVIEW",
        };
        response = await axios.post(`/meetings`, createPayload);
      }

      if (response.data.success) {
        onSuccess && onSuccess(isEdit);
        onClose();
      }
    } catch (error) {
      console.error("Error submitting meeting:", error);
      const errorMessage = error.response?.data?.message || "Failed to submit meeting";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <ArgonBox
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        backdropFilter: "blur(5px)",
      }}
    >
      <ArgonBox
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "20px",
          width: "90%",
          maxWidth: 700,
          maxHeight: "95vh",
          overflowY: "auto",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        {/* Header */}
        <ArgonBox
          sx={{
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
            borderRadius: "20px 20px 0 0",
            p: 3,
            borderBottom: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <ArgonBox
                sx={{
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VideoCallIcon sx={{ color: "white", fontSize: "24px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h5" fontWeight="medium" color="white">
                  {isEdit ? "Edit Meeting" : "Submit Meeting"}
                </ArgonTypography>
                <ArgonTypography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                  Schedule virtual interview meeting
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <ArgonButton
              variant="text"
              onClick={onClose}
              disabled={loading}
              sx={{
                color: "white",
                minWidth: "40px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
                "&:hover": { background: "rgba(255,255,255,0.2)" },
              }}
            >
              ×
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>

        {/* Form Content */}
        <form onSubmit={handleSubmit}>
          <ArgonBox sx={{ background: "white", borderRadius: "0 0 20px 20px", p: 4 }}>
            <Grid container spacing={3}>
              {/* Meeting Link */}
              <Grid item xs={12}>
                <ArgonTypography variant="body2" fontWeight="medium" mb={1} color="#2c3e50">
                  Meeting Link *
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  name="meetingLink"
                  value={formData.meetingLink}
                  onChange={handleChange}
                  placeholder="https://meet.google.com/..."
                  required
                  size="large"
                  disabled={loading}
                />
              </Grid>

              {/* Scheduled Date & Time */}
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="medium" mb={1} color="#2c3e50">
                  Scheduled Date & Time *
                </ArgonTypography>
                <TextField
                  fullWidth
                  name="scheduledDate"
                  type="datetime-local"
                  value={formData.scheduledDate}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#e0e7ff",
                      },
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                />
              </Grid>

              {/* Duration */}
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="medium" mb={1} color="#2c3e50">
                  Duration (minutes) *
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                  size="large"
                  disabled={loading}
                  inputProps={{ min: 15, max: 180, step: 5 }}
                />
              </Grid>

              {/* Meeting Notes */}
              <Grid item xs={12}>
                <ArgonTypography variant="body2" fontWeight="medium" mb={1} color="#2c3e50">
                  Meeting Notes
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  name="meetingNotes"
                  value={formData.meetingNotes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Add any additional notes for the meeting..."
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#e0e7ff",
                      },
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <ArgonButton
                variant="outlined"
                onClick={onClose}
                disabled={loading}
                sx={{
                  borderColor: "#6c757d",
                  color: "#6c757d",
                  borderRadius: "10px",
                  px: 4,
                  py: 1.2,
                  "&:hover": {
                    borderColor: "#495057",
                    backgroundColor: "#f8f9fa",
                  },
                }}
              >
                Cancel
              </ArgonButton>
              <ArgonButton
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  borderRadius: "10px",
                  px: 4,
                  py: 1.2,
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                  "&:hover": {
                    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {loading ? "Submitting..." : isEdit ? "Update Meeting" : "Submit Meeting"}
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        </form>
      </ArgonBox>
    </ArgonBox>
  );
};

MeetingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tutorId: PropTypes.string,
  existingMeeting: PropTypes.shape({
    _id: PropTypes.string,
    meetingLink: PropTypes.string,
    duration: PropTypes.number,
    scheduledDate: PropTypes.string,
    meetingNotes: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

export default MeetingDialog;
